import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {combineLatest, forkJoin, of, Subject} from 'rxjs';
import {filter, map, switchMap, tap} from 'rxjs/operators';
import {ComponentEvent, Page, Pageable, StateAwareComponent} from 'src/app/model/rest/base';
import * as LessonsV3 from 'src/app/model/v3/lessons';
import {LocalStateService} from '../../services/local-state.service';
import {ManagerColV3RestService} from '../../services/rest/v3/manager-col-v3-rest.service';
import {PeopleV3RestService} from '../../services/rest/v3/people-rest.service';

export enum LessonType {
  Upcoming,
  Incomplete,
  Past,
}

export class TeacherLessonsComponentEvent extends ComponentEvent {
}

@Component({
  selector: 'app-teacher-lessons',
  templateUrl: './teacher-lessons.component.html',
  styleUrls: ['./teacher-lessons.component.css'],
  standalone: false,
})
export class TeacherLessonsComponent extends StateAwareComponent<TeacherLessonsComponentEvent> implements OnInit {
  _teacherId: number;
  _lessonType: LessonType;
  _pageable: Pageable;
  hasNext = false;
  _data: Page<LessonsV3.LFTeacherGeneral>;

  _schoolId: number;
  private contextPath: string;
  private studentPhotoUrls = new Map<number, string | null>();

  @Output()
  dataLoaded = new EventEmitter<Page<LessonsV3.LFTeacherGeneral>>();
  @Output()
  currentPageable = new EventEmitter<Pageable>();

  // input subjects
  private teacherIdSubject = new Subject<number>();
  private lessonTypeSubject = new Subject<LessonType>();
  private schoolIdSubject = new Subject<number>();

  constructor(
    private managerV3Rest: ManagerColV3RestService,
    private peopleV3Rest: PeopleV3RestService,
    private localState: LocalStateService
  ) {
    super();

    combineLatest(this.schoolIdSubject, this.teacherIdSubject, this.lessonTypeSubject)
      .pipe(
        filter<[number, number, LessonType]>(
          ([schoolId, teacherId, lessonType]) => schoolId != null && teacherId != null && lessonType != null
        ),
        tap(() => this.clearData()),
        tap(() => this.prepareContextPath()),
        switchMap(() => this.loadCurrentState()),
        switchMap((pageable) => this.loadPage(pageable))
      )
      .subscribe();
  }

  private loadCurrentState() {
    return this.localState.get<Pageable>(this.contextPath, () => {
      const order = this._lessonType === LessonType.Past
        ? ['metric.details.plannedStartDate,DESC']
        : ['metric.details.plannedStartDate,ASC'];
      return Pageable.of(0, 10, order);
    }).pipe(
      tap((pageable) => (this.pageable = pageable))
    );
  }

  private prepareContextPath() {
    this.contextPath = `/school/${this._schoolId}/teachers/${this._teacherId}/lessons/${this._lessonType}`;
  }

  set pageable(value: Pageable) {
    this._pageable = value;
    this.currentPageable.emit(value);
  }

  get pageable() {
    return this._pageable;
  }

  set data(value: Page<LessonsV3.LFTeacherGeneral>) {
    this._data = value;
    this.dataLoaded.emit(value);
  }

  get data() {
    return this._data;
  }

  @Input()
  set teacherId(teacherId: number) {
    this._teacherId = teacherId;
    this.teacherIdSubject.next(teacherId);
  }

  get teacherId(): number {
    return this._teacherId;
  }

  @Input()
  set lessonType(lessonType: LessonType) {
    this._lessonType = lessonType;
    this.lessonTypeSubject.next(lessonType);
  }

  @Input()
  set schoolId(schoolId: number) {
    this._schoolId = schoolId;
    this.schoolIdSubject.next(schoolId);
  }

  @Output()
  lessonDetailsEvent = new EventEmitter<LessonsV3.LFTeacherGeneral>();

  public hasResults() {
    return this.data != null;
  }

  public mayViewLesson(_: LessonsV3.LFTeacherGeneral) {
    return true;
  }

  get lessonType(): LessonType {
    return this._lessonType;
  }

  getPersonProfilePhoto(lesson: LessonsV3.LFTeacherGeneral): string | null {
    const personId = lesson?.students?.[0]?.reference?.person?.id;
    if (!personId) {
      return null;
    }
    return this.studentPhotoUrls.get(personId) ?? null;
  }

  getLessonDate(lesson: LessonsV3.LFTeacherGeneral): Date {
    return lesson?.lessonMetric?.plannedStartDate;
  }

  public getStudentNames(lesson: LessonsV3.LFTeacherGeneral): string {
    return (lesson?.students ?? [])
      .map((student) => student?.reference?.person)
      .map((person) => this.getPersonName(person))
      .join(', ');
  }

  clearData(): void {
    this.pageable = null;
    this.data = null;
    this.studentPhotoUrls.clear();
  }

  ngOnInit() {
    this.eventLoading();
  }

  private loadPage(pageable: Pageable) {
    this.pageable = pageable;
    return this.localState.set<Pageable>(this.contextPath, pageable).pipe(
      switchMap((page) => this.callForLessons(page)),
      switchMap((resultPage) =>
        this.loadStudentPhotos(resultPage).pipe(map(() => resultPage))
      ),
      tap<Page<LessonsV3.LFTeacherGeneral>>((resultPage) => (this.data = resultPage))
    );
  }

  switchPage(pageable: Pageable) {
    this.loadPage(pageable).subscribe();
  }

  public lessonBadgeClass(lesson: LessonsV3.LFTeacherGeneral) {
    if (this._lessonType === undefined) {
      return '';
    }
    return this.getLessonStatusClass(lesson?.status);
  }

  public getInitials(lesson: LessonsV3.LFTeacherGeneral) {
    return this.getInitialsFromPerson(lesson?.students?.[0]?.reference?.person ?? null);
  }

  private callForLessons(pageable: Pageable) {
    const now = new Date();
    const isUpcoming = this._lessonType === LessonType.Upcoming;
    const isPast = this._lessonType === LessonType.Past;

    // Every lesson lands in exactly one card: now splits Upcoming from the other two, then
    // the status decides between Incomplete and Past.
    //
    //   Upcoming   | all eight statuses                                  | planned start >= now
    //   Incomplete | Initializing, Booked, Due, InProgress               | planned start <= now
    //   Past       | Complete, Cancel, InvalidatedByTeacher/ByStudent    | planned start <= now
    const statuses = isUpcoming
      ? [
        LessonsV3.LessonStatus.Initializing,
        LessonsV3.LessonStatus.Booked,
        LessonsV3.LessonStatus.Due,
        LessonsV3.LessonStatus.InProgress,
        LessonsV3.LessonStatus.Complete,
        LessonsV3.LessonStatus.Cancel,
        LessonsV3.LessonStatus.InvalidatedByStudent,
        LessonsV3.LessonStatus.InvalidatedByTeacher,
      ]
      : isPast
        ? [
          LessonsV3.LessonStatus.Complete,
          LessonsV3.LessonStatus.Cancel,
          LessonsV3.LessonStatus.InvalidatedByStudent,
          LessonsV3.LessonStatus.InvalidatedByTeacher,
        ]
        : [
          LessonsV3.LessonStatus.Initializing,
          LessonsV3.LessonStatus.Booked,
          LessonsV3.LessonStatus.Due,
          LessonsV3.LessonStatus.InProgress,
        ];

    return this.managerV3Rest.findLessons(
      'col',
      null,
      'teacher-general',
      null,
      null,
      this.teacherId,
      null,
      null,
      null,
      statuses,
      // upcoming looks forward from now, past and incomplete look backwards
      isUpcoming ? now : null,
      isUpcoming ? null : now,
      pageable
    ).pipe(
      map((page) => ({
        ...page,
        content: (page.content ?? []) as LessonsV3.LFTeacherGeneral[],
      }) as Page<LessonsV3.LFTeacherGeneral>)
    );
  }

  private loadStudentPhotos(page: Page<LessonsV3.LFTeacherGeneral>) {
    const personIds = Array.from(
      new Set(
        (page.content ?? [])
          .map((lesson) => lesson?.students?.[0]?.reference?.person?.id)
          .filter((personId): personId is number => !!personId)
      )
    );
    if (personIds.length === 0) {
      return of(null);
    }

    return forkJoin(
      personIds.map((personId) =>
        this.peopleV3Rest.getPhoto(personId).pipe(
          tap((photo) => {
            this.studentPhotoUrls.set(personId, photo?.publicAccessUrl ? `url(${photo.publicAccessUrl})` : null);
          })
        )
      )
    );
  }

  private getPersonName(person: { name?: string; surname?: string } | null): string {
    if (!person) {
      return '';
    }
    return [person.name, person.surname].filter((part) => !!part).join(' ');
  }

  private getInitialsFromPerson(person: { name?: string; surname?: string } | null): string {
    if (!person) {
      return '';
    }
    return [person.name, person.surname]
      .filter((part) => !!part)
      .map((part) => part[0])
      .join('');
  }

  private getLessonStatusClass(status: LessonsV3.LessonStatus | null | undefined): string {
    switch (status) {
      case LessonsV3.LessonStatus.Booked:
        return 'lesson-status booked';
      case LessonsV3.LessonStatus.Complete:
        return 'lesson-status complete';
      case LessonsV3.LessonStatus.Due:
        return 'lesson-status due';
      case LessonsV3.LessonStatus.InProgress:
        return 'lesson-status in-progress';
      case LessonsV3.LessonStatus.InvalidatedByTeacher:
      case LessonsV3.LessonStatus.InvalidatedByStudent:
      case LessonsV3.LessonStatus.Cancel:
        return 'lesson-status student-invalidate';
      default:
        return '';
    }
  }
}
